import _ from 'lodash';
import moment from 'moment';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  fetchHolterEvents,
  applyGroupEventChange,
  calculateStartStopStripMomentOfListEvent,
  calculateStartStopStripMomentOfListEventV2,
  formatEventStrip,
  updateEventChangeAndNew,
  handleAddEventOthersV2,
} from '@/entities/event';
import { AIECGViewer, CAPTURE_MODE, ECG_VIEWER_CONFIG_STORE } from '@/shared/ui';
import {
  EMITTER_CONSTANTS,
  KEY_RECOIL,
  LIST_EVENT_TYPE_QUERY,
  OTHER_EVENT_LIST,
  TYPE_BEAT_ENUM,
  TYPE_BEAT_EVENT_ENUM,
  TYPE_EVENT_OTHER,
  TYPE_RHYTHM_EVENT_ENUM,
  typesPersistEvent,
} from '@/shared/config';
import { convertToMomentJs, eventTypeConvert } from '@/shared/lib/aiUtils';
import { useDeepCompareEffect, useEmitter, useUpdateEffect } from '@/shared/lib/customHooks';
import { toastrError, toastrSuccess } from '@/shared/lib/toastNotification';
import {
  selectAiChannel,
  selectChartHrsData,
  selectEcgObject,
  selectIsActiveTab,
  selectIsLoadingHrChart,
  selectIsLoadingHrChartOptions,
  selectJumpPortion,
  selectJumpTime,
  selectPickDateAction,
  selectPickedBookmarkDate,
  selectPickedEndDate,
  selectPickedStartDate,
  selectEventFilter,
  selectNextColumnInfo,
  selectMinuteInfoEvents,
  selectPrevColumnInfo,
  selectPreventReloadEcg,
  selectBeatChanges,
  selectEventChanges,
  selectEventNews,
  selectEventOthers,
  selectReloadEcgViewer,
  selectSelectedColumnInfo,
  selectSelectedEvent,
  selectSelectedEventIndex,
  selectBulkBeatsChanges,
  selectGroupEventChanges,
  selectResetBulkBeatsChanges,
  useReportStore,
} from '@/entities/report';
import { TPickDateAction } from '@/shared/model';
import { formatDrawDisplay, formatDrawMutation, checkAndProcessEcgData } from '@/entities/ecg';
import {
  checkAndProcessBeatData,
  countBeatChannels,
  generateUpdatedBeatsV2,
  handleUpdateBeatChanges,
  updateListImagesChangeV2,
} from '@/entities/beat';
import { logError } from '@/shared/lib/apiHelpers';
import { isRangesOverlap } from '@/shared/lib/timeRange';
import { getUpdatedBeatMarkerV2, handleResetAppliedBuldBeats } from '@/widgets/beats/ui/helper';
import type { AfibEcgViewerWrapperProps } from './afibEcgViewerWrapper';

/**
 * Drives the AFib tab's ECG viewer: it loads the ECG samples, beats and overlapping events for the selected minute, builds the viewer object, and exposes the capture, beat-edit and date-picking handlers the wrapper renders. Subscribes to the TAB_1 slice of the report store, redraws on beat, bookmark, filter and event changes while keeping the scroll offset, fetches on selection change through a deep-compare effect, and binds an Escape keyup listener while a date is being picked.
 *
 * @param props - Wrapper props carrying studyId, profileId, ecgDataMap, timezoneOffset and the parent arrow-button handler.
 * @returns An object carrying the loading flags, ecgObject, ecgChartRef, startStripEpoch, event and bookmark indicators, the selected event and channel, the prev/next disabled flags and the capture, beat-update, date-pick and arrow handlers.
 */
const useAfibEcgViewerWrapper = ({
  ecgDataMap,
  profileId,
  studyId,
  timezoneOffset,
  handleClickArrowButton,
}: AfibEcgViewerWrapperProps) => {
  const keyRecoil = KEY_RECOIL.BEAT_HR;
  const isLoadingHrChart = useReportStore(selectIsLoadingHrChart(keyRecoil));
  const isLoadingHrChartOptions = useReportStore(selectIsLoadingHrChartOptions(keyRecoil));
  const selectedColumnInfo = useReportStore(selectSelectedColumnInfo(keyRecoil));
  const selectedEventIndex = useReportStore(selectSelectedEventIndex(keyRecoil));
  const prevColumnInfo = useReportStore(selectPrevColumnInfo(keyRecoil));
  const nextColumnInfo = useReportStore(selectNextColumnInfo(keyRecoil));
  /**
   * Reads the pending edits to existing events for the AFib tab. Pulls it straight off the report store's current state via getState so callers see the value at call time rather than at render time.
   *
   * @returns The event changes for the AFib tab.
   */
  const getEventChanges = () => selectEventChanges(keyRecoil)(useReportStore.getState());
  const setEventChangesAction = useReportStore((s) => s.actions.setEventChanges);
  /**
   * Writes the pending edits to existing events for the AFib tab. Delegates to the report-store action, keyed to the AFib tab.
   *
   * @param v - The next the pending edits to existing events, or an updater receiving the current one.
   * @returns Whatever the underlying report-store action returns after writing the value.
   */
  const setEventChanges = (v: Parameters<typeof setEventChangesAction>[1]) =>
    setEventChangesAction(keyRecoil, v);
  const eventChanges = useReportStore(selectEventChanges(keyRecoil));
  /**
   * Reads the events created locally and not yet persisted for the AFib tab. Pulls it straight off the report store's current state via getState so callers see the value at call time rather than at render time.
   *
   * @returns The newly added events for the AFib tab.
   */
  const getEventNews = () => selectEventNews(keyRecoil)(useReportStore.getState());
  const setEventNewsAction = useReportStore((s) => s.actions.setEventNews);
  /**
   * Writes the locally created events for the AFib tab. Delegates to the report-store action, keyed to the AFib tab.
   *
   * @param v - The next the locally created events, or an updater receiving the current one.
   * @returns Whatever the underlying report-store action returns after writing the value.
   */
  const setEventNews = (v: Parameters<typeof setEventNewsAction>[1]) =>
    setEventNewsAction(keyRecoil, v);
  /**
   * Reads the pending per-beat edits for the AFib tab. Pulls it straight off the report store's current state via getState so callers see the value at call time rather than at render time.
   *
   * @returns The recorded beat changes for the AFib tab.
   */
  const getBeatChanges = () => selectBeatChanges(keyRecoil)(useReportStore.getState());
  const setBeatChangesAction = useReportStore((s) => s.actions.setBeatChanges);
  /**
   * Writes the pending per-beat edits for the AFib tab. Delegates to the report-store action, keyed to the AFib tab.
   *
   * @param v - The next the pending per-beat edits, or an updater receiving the current one.
   * @returns Whatever the underlying report-store action returns after writing the value.
   */
  const setBeatChanges = (v: Parameters<typeof setBeatChangesAction>[1]) =>
    setBeatChangesAction(keyRecoil, v);
  const pickDateAction = useReportStore(selectPickDateAction(keyRecoil));
  const setPickDateActionAction = useReportStore((s) => s.actions.setPickDateAction);
  /**
   * Writes the active date-picking action for the AFib tab. Delegates to the report-store action, keyed to the AFib tab.
   *
   * @param v - The next the active date-picking action, or an updater receiving the current one.
   * @returns Whatever the underlying report-store action returns after writing the value.
   */
  const setPickDateAction = (v: Parameters<typeof setPickDateActionAction>[1]) =>
    setPickDateActionAction(keyRecoil, v);
  const pickedStartDate = useReportStore(selectPickedStartDate(keyRecoil));
  const setPickedStartDateAction = useReportStore((s) => s.actions.setPickedStartDate);
  /**
   * Writes the picked start date for the AFib tab. Delegates to the report-store action, keyed to the AFib tab.
   *
   * @param v - The next the picked start date, or an updater receiving the current one.
   * @returns Whatever the underlying report-store action returns after writing the value.
   */
  const setPickedStartDate = (v: Parameters<typeof setPickedStartDateAction>[1]) =>
    setPickedStartDateAction(keyRecoil, v);
  const pickedEndDate = useReportStore(selectPickedEndDate(keyRecoil));
  const setPickedEndDateAction = useReportStore((s) => s.actions.setPickedEndDate);
  /**
   * Writes the picked end date for the AFib tab. Delegates to the report-store action, keyed to the AFib tab.
   *
   * @param v - The next the picked end date, or an updater receiving the current one.
   * @returns Whatever the underlying report-store action returns after writing the value.
   */
  const setPickedEndDate = (v: Parameters<typeof setPickedEndDateAction>[1]) =>
    setPickedEndDateAction(keyRecoil, v);
  const pickedBookmarkDate = useReportStore(selectPickedBookmarkDate(keyRecoil));
  const setPickedBookmarkDateAction = useReportStore((s) => s.actions.setPickedBookmarkDate);
  /**
   * Writes the picked bookmark date for the AFib tab. Delegates to the report-store action, keyed to the AFib tab.
   *
   * @param v - The next the picked bookmark date, or an updater receiving the current one.
   * @returns Whatever the underlying report-store action returns after writing the value.
   */
  const setPickedBookmarkDate = (v: Parameters<typeof setPickedBookmarkDateAction>[1]) =>
    setPickedBookmarkDateAction(keyRecoil, v);
  const aiChannel = useReportStore(selectAiChannel(keyRecoil));
  const setAiChannelAction = useReportStore((s) => s.actions.setAiChannel);
  /**
   * Writes the detected AI beat channel for the AFib tab. Delegates to the report-store action, keyed to the AFib tab.
   *
   * @param v - The next the detected AI beat channel, or an updater receiving the current one.
   * @returns Whatever the underlying report-store action returns after writing the value.
   */
  const setAiChannel = (v: Parameters<typeof setAiChannelAction>[1]) =>
    setAiChannelAction(keyRecoil, v);
  const reloadEcgViewer = useReportStore(selectReloadEcgViewer(keyRecoil));
  const ecgObject = useReportStore(selectEcgObject(keyRecoil));
  const setEcgObjectAction = useReportStore((s) => s.actions.setEcgObject);
  /**
   * Writes the ECG viewer object for the AFib tab. Delegates to the report-store action, keyed to the AFib tab.
   *
   * @param v - The next the ECG viewer object, or an updater receiving the current one.
   * @returns Whatever the underlying report-store action returns after writing the value.
   */
  const setEcgObject = (v: Parameters<typeof setEcgObjectAction>[1]) =>
    setEcgObjectAction(keyRecoil, v);
  const isActiveTab = useReportStore(selectIsActiveTab(keyRecoil));
  /**
   * Reads the per-minute heart-rate series backing the HR chart for the AFib tab. Pulls it straight off the report store's current state via getState so callers see the value at call time rather than at render time.
   *
   * @returns The heart-rate chart data for the AFib tab.
   */
  const getChartHrsData = () => selectChartHrsData(keyRecoil)(useReportStore.getState());
  /**
   * Reads the free-form other events captured as strips for the AFib tab. Pulls it straight off the report store's current state via getState so callers see the value at call time rather than at render time.
   *
   * @returns The other events for the AFib tab.
   */
  const getEventOthers = () => selectEventOthers(keyRecoil)(useReportStore.getState());
  const eventOthers = useReportStore(selectEventOthers(keyRecoil));
  const setEventOthersAction = useReportStore((s) => s.actions.setEventOthers);
  /**
   * Writes the free-form other events for the AFib tab. Delegates to the report-store action, keyed to the AFib tab.
   *
   * @param v - The next the free-form other events, or an updater receiving the current one.
   * @returns Whatever the underlying report-store action returns after writing the value.
   */
  const setEventOthers = (v: Parameters<typeof setEventOthersAction>[1]) =>
    setEventOthersAction(keyRecoil, v);
  const jumpTime = useReportStore(selectJumpTime(keyRecoil));
  const setJumpTimeAction = useReportStore((s) => s.actions.setJumpTime);
  /**
   * Writes the timestamp the viewer should jump to for the AFib tab. Delegates to the report-store action, keyed to the AFib tab.
   *
   * @param v - The next the timestamp the viewer should jump to, or an updater receiving the current one.
   * @returns Whatever the underlying report-store action returns after writing the value.
   */
  const setJumpTime = (v: Parameters<typeof setJumpTimeAction>[1]) =>
    setJumpTimeAction(keyRecoil, v);
  /**
   * Reads the window portion used when jumping the ECG viewer for the AFib tab. Pulls it straight off the report store's current state via getState so callers see the value at call time rather than at render time.
   *
   * @returns The jump portion for the AFib tab.
   */
  const getJumpPortion = () => selectJumpPortion(keyRecoil)(useReportStore.getState());
  const setJumpPortionAction = useReportStore((s) => s.actions.setJumpPortion);
  /**
   * Writes the window portion used when jumping the viewer for the AFib tab. Delegates to the report-store action, keyed to the AFib tab.
   *
   * @param v - The next the window portion used when jumping the viewer, or an updater receiving the current one.
   * @returns Whatever the underlying report-store action returns after writing the value.
   */
  const setJumpPortion = (v: Parameters<typeof setJumpPortionAction>[1]) =>
    setJumpPortionAction(keyRecoil, v);
  /**
   * Reads the grouped event changes applied across a selection for the AFib tab. Pulls it straight off the report store's current state via getState so callers see the value at call time rather than at render time.
   *
   * @returns The group event changes for the AFib tab.
   */
  const getGroupEventChanges = () => selectGroupEventChanges(keyRecoil)(useReportStore.getState());
  const ecgBookmarks = useReportStore((s) => s.ecgBookmarks);
  // selectedEventState (Recoil/index.ts, Task 7) is typed Array<IEvent | IPoint>, but every
  // consumer here (and elsewhere in this slice) treats it as a single event object - Task 7 atom-type gap.
  const selectedEvent = useReportStore(selectSelectedEvent(keyRecoil)) as any;
  /**
   * Reads the currently selected event for the AFib tab. Pulls it straight off the report store's current state via getState so callers see the value at call time rather than at render time.
   *
   * @returns The selected event for the AFib tab.
   */
  const getSelectedEvent = () =>
    selectSelectedEvent(keyRecoil)(useReportStore.getState()) as unknown as any;
  const setSelectedEventInfoAction = useReportStore((s) => s.actions.setSelectedEventInfo);
  /**
   * Writes the selected event info list for the AFib tab. Delegates to the report-store action, keyed to the AFib tab.
   *
   * @param v - The next the selected event info list, or an updater receiving the current one.
   * @returns Whatever the underlying report-store action returns after writing the value.
   */
  const setSelectedEventInfo = (v: Parameters<typeof setSelectedEventInfoAction>[1]) =>
    setSelectedEventInfoAction(keyRecoil, v);
  const setIsLoadingEcgViewerAction = useReportStore((s) => s.actions.setIsLoadingEcgViewer);
  /**
   * Writes the ECG viewer loading flag for the AFib tab. Delegates to the report-store action, keyed to the AFib tab.
   *
   * @param v - The next the ECG viewer loading flag, or an updater receiving the current one.
   * @returns Whatever the underlying report-store action returns after writing the value.
   */
  const setIsLoadingEcgViewer = (v: Parameters<typeof setIsLoadingEcgViewerAction>[1]) =>
    setIsLoadingEcgViewerAction(keyRecoil, v);
  const setMinuteInfoEventsAction = useReportStore((s) => s.actions.setMinuteInfoEvents);
  /**
   * Writes the minute-info events for the AFib tab. Delegates to the report-store action, keyed to the AFib tab.
   *
   * @param v - The next the minute-info events, or an updater receiving the current one.
   * @returns Whatever the underlying report-store action returns after writing the value.
   */
  const setMinuteInfoEvents = (v: Parameters<typeof setMinuteInfoEventsAction>[1]) =>
    setMinuteInfoEventsAction(keyRecoil, v);
  /**
   * Reads the minute-info events shown alongside the selected minute for the AFib tab. Pulls it straight off the report store's current state via getState so callers see the value at call time rather than at render time.
   *
   * @returns The minute-info events for the AFib tab.
   */
  const getMinuteInfoEvents = () => selectMinuteInfoEvents(keyRecoil)(useReportStore.getState());
  /**
   * Reads the flag that suppresses the next ECG reload for the AFib tab. Pulls it straight off the report store's current state via getState so callers see the value at call time rather than at render time.
   *
   * @returns The prevent-reload flag for the AFib tab.
   */
  const getPreventReloadEcg = () => selectPreventReloadEcg(keyRecoil)(useReportStore.getState());
  const setPreventReloadEcgAction = useReportStore((s) => s.actions.setPreventReloadEcg);
  /**
   * Writes the flag that suppresses the next ECG reload for the AFib tab. Delegates to the report-store action, keyed to the AFib tab.
   *
   * @param v - The next the flag that suppresses the next ECG reload, or an updater receiving the current one.
   * @returns Whatever the underlying report-store action returns after writing the value.
   */
  const setPreventReloadEcg = (v: Parameters<typeof setPreventReloadEcgAction>[1]) =>
    setPreventReloadEcgAction(keyRecoil, v);
  const isRenderViewerEcg = useReportStore((s) => s.isRenderViewerEcg);

  const setEventOptionsAction = useReportStore((s) => s.actions.setEventOptions);
  /**
   * Writes the event options snapshot used for undo and redo for the AFib tab. Delegates to the report-store action, keyed to the AFib tab.
   *
   * @param v - The next the event options snapshot used for undo and redo, or an updater receiving the current one.
   * @returns Whatever the underlying report-store action returns after writing the value.
   */
  const setEventOptions = (v: Parameters<typeof setEventOptionsAction>[1]) =>
    setEventOptionsAction(keyRecoil, v);
  const beatChanges = useReportStore(selectBeatChanges(keyRecoil));
  /**
   * Reads the bulk beat reclassifications awaiting application for the AFib tab. Pulls it straight off the report store's current state via getState so callers see the value at call time rather than at render time.
   *
   * @returns The bulk beat changes for the AFib tab.
   */
  const getBulkBeatsChanges = () => selectBulkBeatsChanges(keyRecoil)(useReportStore.getState());
  /**
   * Reads the record of bulk beat changes already reverted for the AFib tab. Pulls it straight off the report store's current state via getState so callers see the value at call time rather than at render time.
   *
   * @returns The reset bulk-beat changes for the AFib tab.
   */
  const getResetBulkBeatsChanges = () =>
    selectResetBulkBeatsChanges(keyRecoil)(useReportStore.getState());
  const setBeatOptionsAction = useReportStore((s) => s.actions.setBeatOptions);
  /**
   * Writes the beat options snapshot used for undo and redo for the AFib tab. Delegates to the report-store action, keyed to the AFib tab.
   *
   * @param v - The next the beat options snapshot used for undo and redo, or an updater receiving the current one.
   * @returns Whatever the underlying report-store action returns after writing the value.
   */
  const setBeatOptions = (v: Parameters<typeof setBeatOptionsAction>[1]) =>
    setBeatOptionsAction(keyRecoil, v);
  const setResetBulkBeatsChangesAction = useReportStore((s) => s.actions.setResetBulkBeatsChanges);
  /**
   * Writes the record of bulk beat changes already reverted for the AFib tab. Delegates to the report-store action, keyed to the AFib tab.
   *
   * @param v - The next the record of bulk beat changes already reverted, or an updater receiving the current one.
   * @returns Whatever the underlying report-store action returns after writing the value.
   */
  const setResetBulkBeatsChanges = (v: Parameters<typeof setResetBulkBeatsChangesAction>[1]) =>
    setResetBulkBeatsChangesAction(keyRecoil, v);
  const eventFilter = useReportStore(selectEventFilter(keyRecoil));

  const [isLoading, setIsLoading] = useState(false);
  const [isDisablePrevBtn, setIsDisablePrevBtn] = useState(false);
  const [isDisableNextBtn, setIsDisableNextBtn] = useState(false);
  const ecgChartRef = useRef<AIECGViewer>(null);
  const startStripEpoch = useRef<any>(null);
  const stopStripEpoch = useRef<any>(null);
  const originalBeatMarkers = useRef<any[]>([]);
  const originalHesBeatStatus = useRef<any[]>([]);
  const originalBeatHeatMapIds = useRef<any[]>([]);
  const originalBeatTemplateIds = useRef<any[]>([]);
  const beatMarkersInEcgViewer = useRef<any[]>([]);
  const hesBeatStatusInEcgViewer = useRef<any[]>([]);
  const isJumpToEnd = useRef(false);
  const isJumpToStart = useRef(false);
  const currentQueryEventHour = useRef<any>(null);
  const preventReloadEcgViewer = useRef(false);
  const keepEcgPosition = useRef(false);
  const savedEvents = useRef<any[]>([]);
  const ecgObjectRef = useRef<any>({});
  const currentMinute = useRef<any>();
  const currentBeatsChangeRef = useRef<any>();

  //* prevent api race condition by not setState data of old selected strip or event
  let isActiveQuery: boolean;
  const isPickingDate =
    pickDateAction === TPickDateAction.START ||
    pickDateAction === TPickDateAction.END ||
    pickDateAction === TPickDateAction.BOOKMARK;

  const getEventInfoInEcgViewer = ({
    event,
    isSelected = false,
    selectedStrip,
  }: {
    event: any;
    isSelected?: boolean;
    selectedStrip?: any;
  }) => {
    const { samplingFrequency } = ecgDataMap;
    const isEvent = event.start && event.stop; // not minute info (minute info dont have start and stop)
    let startPos = 0;
    let stopPos = 0;
    let color;
    if (isEvent) {
      const isArtifactEvent = event.type === TYPE_RHYTHM_EVENT_ENUM.ARTIFACT;
      const isAfibEvent = event.type === TYPE_RHYTHM_EVENT_ENUM.AFIB;
      color = isArtifactEvent ? 'gray' : isAfibEvent ? 'orange' : 'red';
      const startEpoch = new Date(event.start).getTime();
      const stopEpoch = new Date(event.stop).getTime();
      if (startStripEpoch.current <= startEpoch) {
        const eventDiffSeconds = Math.abs(startEpoch - startStripEpoch.current) / 1000;
        startPos = Math.round(eventDiffSeconds * samplingFrequency);
      }
      if (stopEpoch >= stopStripEpoch.current) {
        stopPos = (stopStripEpoch.current - startStripEpoch.current) / 1000;
      } else {
        stopPos = (stopEpoch - startStripEpoch.current) / 1000;
      }
      stopPos = Math.round(stopPos * samplingFrequency);
    }
    return {
      type:
        isEvent && event.type === TYPE_EVENT_OTHER
          ? event.title
          : isEvent && color === 'red'
            ? (eventTypeConvert as any)[event.type]
            : '', // Show event type text in ecg viewer for others event
      startPos,
      stopPos,
      color,
      id: event.id,
      isSelected,
      portion: (TYPE_BEAT_EVENT_ENUM as any)[event.type] ? 2 : 10, // Determine start window when jump to
      isHighLight: selectedStrip?.idEvent === event.idEvent,
    };
  };

  const createObjectEcg = ({
    ecg,
    beats,
    hesBeatStatus,
    events,
    eventIndex,
    beatHeatMapIds,
    exportEcgData,
    beatTemplateIds,
  }: {
    ecg: any;
    beats: any;
    hesBeatStatus: any;
    events: any;
    eventIndex: any;
    beatHeatMapIds: any;
    exportEcgData: any;
    beatTemplateIds: any;
  }) => {
    const { channels, gain, samplingFrequency } = ecgDataMap;
    // *: Add capture sample mark
    let drawStartSample = 1;
    const selectedEv = getSelectedEvent();
    const findEvent = _.find(events, (x: any) => x.idEvent === selectedEv?.idEvent);
    let drawList: any = [];
    if (findEvent?.type) {
      drawList = findEvent?.draw;
    } else {
      // isRemovedStrip is read here but IEventOther (Recoil/model.ts, Task 7) never declares it - pre-existing.
      const otherEvents = getEventOthers() as any[];
      const filterRemoveOther = _.filter(otherEvents, (x) => !x.isRemovedStrip);
      drawList =
        _.filter(_.flatten(_.map(filterRemoveOther, (event) => event.draw)), (x) => !!x) || [];
    }
    const eventsInfo = _.map(events, (event, index) =>
      getEventInfoInEcgViewer({
        event,
        isSelected: index === eventIndex,
        selectedStrip: findEvent,
      }),
    );
    const startSampleEpoch = moment(findEvent?.start).valueOf();
    if (
      startSampleEpoch >= startStripEpoch.current &&
      startSampleEpoch <= stopStripEpoch.current &&
      findEvent?.type
    ) {
      const additionalSample = (TYPE_BEAT_EVENT_ENUM as any)[findEvent?.type]
        ? 5 * samplingFrequency
        : samplingFrequency;
      const startSample =
        Math.round(((startSampleEpoch - startStripEpoch.current) / 1000) * samplingFrequency) -
        additionalSample;
      drawStartSample = startSample > 1 ? startSample : 1;
    }
    const { draw } = formatDrawDisplay(
      drawList,
      startStripEpoch.current,
      samplingFrequency,
      stopStripEpoch.current,
    );
    const viewerObject = {
      options: {},
      widgets: draw,
    };
    const { updatedBeats, updatedHesBeatStatus } = generateUpdatedBeatsV2({
      beats,
      hesBeatStatus,
      beatChanges: getBeatChanges(),
      startEpoch: startStripEpoch.current,
      stopEpoch: stopStripEpoch.current,
      beatHeatMapIds,
      beatTemplateIds,
      applyBulkBeatsChanges: getBulkBeatsChanges(),
      resetAppliedBulkBeatsChanges: getResetBulkBeatsChanges(),
    });
    beatMarkersInEcgViewer.current = updatedBeats;
    hesBeatStatusInEcgViewer.current = updatedHesBeatStatus;
    const endStripPos =
      ((stopStripEpoch.current - startStripEpoch.current) / 1000) * samplingFrequency;
    savedEvents.current = _.cloneDeep(events);
    const newEcgObject = {
      dataSamples: ecg,
      adcGain: gain,
      adcZero: 0,
      numChannels: channels.length,
      viewerObject,
      diagnosticLead: 2,
      samplingRate: samplingFrequency,
      events: eventsInfo,
      aiPredict: [
        {
          beats: updatedBeats,
          hesBeatStatus: updatedHesBeatStatus,
          channel: 1,
          hesEventStatus: _.times(updatedHesBeatStatus.length, () => 1),
        },
      ],
      isAppliedAIByDefault: true,
      isShowVES: true,
      isShowSVES: true,
      isShowBeatMarkers: true,
      jumpOffset: keepEcgPosition.current
        ? ecgChartRef.current?.getOffsetX()
        : isJumpToStart.current
          ? 1
          : isJumpToEnd.current
            ? endStripPos
            : drawStartSample,
      hiddenChannels: ECG_VIEWER_CONFIG_STORE.visibleChannels,
      hpFilters: ECG_VIEWER_CONFIG_STORE.hiPValue,
      notchFilters: ECG_VIEWER_CONFIG_STORE.loPValue,
      speed: ECG_VIEWER_CONFIG_STORE.spdValue,
      amp: ECG_VIEWER_CONFIG_STORE.ampValue,
      wndDuration: ECG_VIEWER_CONFIG_STORE.wndDuration,
      exportEcgData,
    };
    setEcgObject(newEcgObject);
    keepEcgPosition.current = false;
    ecgObjectRef.current = newEcgObject;
  };

  /**
   * Loads and assembles everything the ECG viewer needs for one selected minute of the AFib recording. Bails out when a reload is suppressed, fetches the hour's holter events plus ECG samples and beats in parallel, merges overlapping prev/next column events and pending event changes, trims samples and beats to the strip window, derives the AI channel, then builds the viewer object while toggling loading flags and logging non-cancelled failures.
   *
   * @param selectedEvents - The selected column's events, whose last entry supplies the minute being loaded.
   * @param eventIndex - Index within the events list to mark as selected in the viewer.
   * @returns A promise resolving once the viewer object for the minute has been built or the load has been skipped or has failed.
   */
  const downloadDataEcgV2 = async (selectedEvents: any[], eventIndex = 0) => {
    if (_.isEmpty(selectedEvents)) {
      return;
    }

    if (preventReloadEcgViewer.current) {
      preventReloadEcgViewer.current = false;
      return;
    }

    if (getPreventReloadEcg()) {
      setPreventReloadEcg(false);
      return;
    }

    if (currentMinute.current !== selectedEvents[selectedEvents.length - 1].startTimeEpoch) {
      setIsLoading(true);
      currentMinute.current = selectedEvents[selectedEvents.length - 1].startTimeEpoch;
    }
    setIsLoadingEcgViewer(true);
    try {
      const events = [...selectedEvents];
      const minuteInfo = events[events.length - 1];
      const startHours = moment(minuteInfo.startTimeEpoch).startOf('hours');
      const eventOthers = getEventOthers();
      //* default will fetch minute - 10s -> minute + 70s for cache
      const { defaultStartStrip, defaultStopStrip } = calculateStartStopStripMomentOfListEventV2({
        startTimeEpoch: minuteInfo.startTimeEpoch,
      });
      const startSearchDate = startHours.toISOString();
      const stopSearchDate = startHours.clone().add(1, 'hours').toISOString();
      currentQueryEventHour.current = startSearchDate;

      const { events: downloadEvents } = await fetchHolterEvents(
        {
          studyId,
          profileId,
          types: LIST_EVENT_TYPE_QUERY,
          startSearchDate,
          stopSearchDate,
        },
        0,
        true,
        null,
        false,
      );

      const [ecgData, holterBeats] = await Promise.all([
        checkAndProcessEcgData({
          ecgDataMap,
          dataLength:
            ((defaultStopStrip.valueOf() - defaultStartStrip.valueOf()) / 1000) *
            ecgDataMap.samplingFrequency,
          // checkAndProcessEcgData's momentObject type (common/handlerEcgData.ts, Task 7) declares
          // string | number, but the function itself does moment(momentObject.startMoment) - it accepts Moment too.
          momentObject: {
            startMoment: defaultStartStrip,
            stopMoment: defaultStopStrip,
          } as any,
        }),
        checkAndProcessBeatData({
          ecgDataMap,
          momentObject: {
            startMoment: defaultStartStrip,
            stopMoment: defaultStopStrip,
          } as any,
        }),
      ]);

      if (isActiveQuery) {
        const startMinuteString = moment(minuteInfo.startTimeEpoch).toISOString();
        const stopMinuteString = moment(minuteInfo.startTimeEpoch).add(60, 'seconds').toISOString();
        const downloadEventsFormat = formatEventStrip(downloadEvents);
        const downloadEventsUpdate = updateEventChangeAndNew(
          downloadEventsFormat,
          getEventChanges(),
          getEventNews(),
          startSearchDate,
          stopSearchDate,
          typesPersistEvent,
          true,
        );
        applyGroupEventChange(downloadEventsUpdate, getGroupEventChanges());
        // isRangesOverlap (handler.ts, Task 7) declares number params but every call site here
        // passes ISO date strings, which compare correctly lexicographically - pre-existing.
        _.forEach(downloadEventsUpdate, (downloadEvent: any) => {
          if (
            isRangesOverlap(
              downloadEvent.start,
              downloadEvent.stop,
              startMinuteString as any,
              stopMinuteString as any,
            )
          ) {
            downloadEvent.startTimeEpoch = minuteInfo.startTimeEpoch;
            events.push(downloadEvent);
          }
        });

        const { startStrip, stopStrip } = calculateStartStopStripMomentOfListEvent({
          events,
        });
        const startTimeString = startStrip.toISOString();
        const stopTimeString = stopStrip.toISOString();
        _.forEach(nextColumnInfo, (event: any) => {
          if (
            !_.find(events, (x) => x.idEvent === event.idEvent) &&
            isRangesOverlap(event.start, event.stop, startTimeString as any, stopTimeString as any)
          ) {
            events.push(event);
          }
        });
        _.forEach(prevColumnInfo, (event: any) => {
          if (
            !_.find(events, (x) => x.idEvent === event.idEvent) &&
            isRangesOverlap(event.start, event.stop, startTimeString as any, stopTimeString as any)
          ) {
            events.push(event);
          }
        });

        const selectedEventInfo = [
          ..._.map(events, (event) => ({
            ...event,
            startTimeEpoch: minuteInfo.startTimeEpoch,
          })).filter(Boolean),
        ];
        const sortedSelectedEventInfo = _.sortBy(selectedEventInfo, [
          (event) => event.type !== TYPE_RHYTHM_EVENT_ENUM.AFIB,
          (event) => event?.start,
          (event) => !OTHER_EVENT_LIST.includes(event.type),
          (event) => event.type !== TYPE_RHYTHM_EVENT_ENUM.ARTIFACT,
        ]);
        setSelectedEventInfo(
          _.uniqBy(
            _.filter(sortedSelectedEventInfo, (x) => !x.isNew && x.type !== TYPE_EVENT_OTHER),
            'idEvent',
          ),
        );
        const otherEvents = _.filter(sortedSelectedEventInfo, (x) => x.type === TYPE_EVENT_OTHER);
        const minuteInfoEvs = _.uniqBy([...eventOthers, ...otherEvents], 'id');
        setMinuteInfoEvents(minuteInfoEvs);

        startStripEpoch.current = startStrip.valueOf();
        stopStripEpoch.current = stopStrip.valueOf();
        // * Cut ecgdata and beat in range
        const cuttedMsStart = startStripEpoch.current - defaultStartStrip.valueOf();
        const cuttedMsStop = defaultStopStrip.valueOf() - stopStripEpoch.current;
        const cuttedSampleStart = (cuttedMsStart / 1000) * ecgDataMap.samplingFrequency;
        const cuttedSampleStop = (cuttedMsStop / 1000) * ecgDataMap.samplingFrequency;
        const cuttedEcgData = _.map(ecgData as any[], (subArr: any) =>
          subArr.slice(cuttedSampleStart, subArr.length - cuttedSampleStop),
        );
        const cuttedBeatSample: any[] = [];
        const cuttedHesBeatStatus: any[] = [];
        const cuttedBeatHeatMapIds: any[] = [];
        const cuttedTemplateIds: any[] = [];

        const cuttedBeatChannel: any[] = [];
        if (!_.isEmpty(holterBeats?.beats)) {
          _.forEach(holterBeats.beats, (sample: any, index) => {
            const epochBeat =
              defaultStartStrip.valueOf() + (sample * 1000) / ecgDataMap.samplingFrequency;
            if (epochBeat >= startStripEpoch.current && epochBeat <= stopStripEpoch.current) {
              cuttedHesBeatStatus.push(holterBeats?.hesBeatStatus[index]);
              cuttedBeatChannel.push(holterBeats?.beatChannels[index]);
              cuttedBeatSample.push(
                Math.round(
                  ((epochBeat - startStripEpoch.current) / 1000) * ecgDataMap.samplingFrequency,
                ),
              );
              cuttedBeatHeatMapIds.push(holterBeats?.beatHeatMapIds[index]);
              cuttedTemplateIds.push(holterBeats?.beatTemplateIds[index]);
            }
          });
        }
        const cuttedHolterBeats = {
          beats: cuttedBeatSample,
          hesBeatStatus: cuttedHesBeatStatus,
          beatChannels: cuttedBeatChannel,
          beatHeatMapIds: cuttedBeatHeatMapIds,
          beatTemplateIds: cuttedTemplateIds,
        };
        // countBeatChannels returns a number but aiChannelState (Task 7) is typed TAiChannel | null - pre-existing.
        setAiChannel(countBeatChannels(cuttedHolterBeats?.beatChannels) as any);
        const eventsInfo = _.filter(events, (x) => x.type !== TYPE_EVENT_OTHER);

        const exportStart = startStripEpoch.current - 10000;
        const exportStop = stopStripEpoch.current + 10000;
        const ecgExport = await checkAndProcessEcgData({
          ecgDataMap,
          dataLength: ((exportStart - exportStop) / 1000) * ecgDataMap.samplingFrequency,
          momentObject: {
            startMoment: moment(exportStart),
            stopMoment: moment(exportStop),
          } as any,
        });
        createObjectEcg({
          ecg: cuttedEcgData,
          beats: cuttedHolterBeats?.beats,
          hesBeatStatus: cuttedHolterBeats?.hesBeatStatus,
          events: _.uniqWith(eventsInfo, (a, b) => {
            const aCopy = { id: a?.id, type: a?.type, idEvent: a?.idEvent };
            const bCopy = { id: b?.id, type: b?.type, idEvent: b?.idEvent };
            return _.isEqual(aCopy, bCopy);
          }),
          eventIndex,
          beatHeatMapIds: cuttedHolterBeats?.beatHeatMapIds,
          beatTemplateIds: cuttedHolterBeats?.beatTemplateIds,
          exportEcgData: {
            ecgData: ecgExport,
          },
        });
        originalBeatMarkers.current = cuttedHolterBeats?.beats;
        if (cuttedHolterBeats?.hesBeatStatus) {
          originalHesBeatStatus.current = [...cuttedHolterBeats.hesBeatStatus];
        }
        originalBeatHeatMapIds.current = cuttedHolterBeats?.beatHeatMapIds;
        originalBeatTemplateIds.current = cuttedHolterBeats?.beatTemplateIds;
        const hrsData = getChartHrsData();
        const prevMinute = minuteInfo.startTimeEpoch - 60000;
        const nextMinute = minuteInfo.startTimeEpoch + 60000;
        const prevMinuteHrData = _.find(hrsData, (x) => x.id === prevMinute);
        const nextMinuteHrData = _.find(hrsData, (x) => x.id === nextMinute);
        setIsDisablePrevBtn(!prevMinuteHrData?.avgHr);
        setIsDisableNextBtn(!nextMinuteHrData?.avgHr);
        setIsLoading(false);
        setIsLoadingEcgViewer(false);
      }

      isJumpToEnd.current = false;
      isJumpToStart.current = false;
    } catch (error) {
      if (!_.includes(error.message, 'canceled')) {
        logError('Failed to download ECG data: ', error);
      }

      setIsLoading(false);
      setIsLoadingEcgViewer(false);
    }
  };

  /**
   * Rebuilds the ECG object from the originally downloaded beats so the viewer reflects current beat edits and display settings. Regenerates beats and HES statuses from the pending, bulk and reset beat changes, then clones the existing ECG object with the current filter, speed, amplitude and channel settings and stores it.
   *
   * @returns Nothing.
   */
  const reCreateObjectEcg = () => {
    if (!_.isEmpty(ecgObject)) {
      const { updatedBeats, updatedHesBeatStatus } = generateUpdatedBeatsV2({
        beats: originalBeatMarkers.current,
        beatHeatMapIds: originalBeatHeatMapIds.current,
        hesBeatStatus: originalHesBeatStatus.current,
        beatTemplateIds: originalBeatTemplateIds.current,
        beatChanges: getBeatChanges(),
        startEpoch: startStripEpoch.current,
        stopEpoch: stopStripEpoch.current,
        applyBulkBeatsChanges: getBulkBeatsChanges(),
        resetAppliedBulkBeatsChanges: getResetBulkBeatsChanges(),
      });
      beatMarkersInEcgViewer.current = updatedBeats;
      hesBeatStatusInEcgViewer.current = updatedHesBeatStatus;
      const clonedEcgObject = _.cloneDeep(ecgObject);
      _.assign(clonedEcgObject, {
        hiddenChannels: ECG_VIEWER_CONFIG_STORE.visibleChannels,
        hpFilters: ECG_VIEWER_CONFIG_STORE.hiPValue,
        notchFilters: ECG_VIEWER_CONFIG_STORE.loPValue,
        speed: ECG_VIEWER_CONFIG_STORE.spdValue,
        amp: ECG_VIEWER_CONFIG_STORE.ampValue,
        wndDuration: ECG_VIEWER_CONFIG_STORE.wndDuration,
        aiPredict: [
          {
            beats: updatedBeats,
            hesBeatStatus: updatedHesBeatStatus,
            channel: 1,
            hesEventStatus: _.times(updatedHesBeatStatus.length, () => 1),
          },
        ],
      });
      setEcgObject(clonedEcgObject);
      ecgObjectRef.current = clonedEcgObject;
    }
  };

  /**
   * Converts a click position inside the ECG strip into an ISO timestamp and stores it as the picked start, end or bookmark date. Only acts while a pick is in progress, offsetting the strip's start epoch by the given seconds in the report timezone and routing the result by the active pick action.
   *
   * @param diffSeconds - Seconds from the start of the displayed strip to the picked position.
   * @returns Nothing.
   */
  const handlePickDate = (diffSeconds: number) => {
    if (isPickingDate) {
      const date = convertToMomentJs(startStripEpoch.current, timezoneOffset)
        .add(diffSeconds, 'seconds')
        .toISOString();
      switch (pickDateAction) {
        case TPickDateAction.START: {
          setPickedStartDate(date);
          break;
        }
        case TPickDateAction.END: {
          setPickedEndDate(date);
          break;
        }
        case TPickDateAction.BOOKMARK: {
          setPickedBookmarkDate(date);
          break;
        }
        default: {
          break;
        }
      }
    }
  };

  /**
   * Refreshes the ECG viewer's event overlay and strip drawings after a capture or a change of selected event. Recomputes each event's position and colour against the strip window, picks the draw list from the selected event's pending changes or from the other events, regenerates beats from the beat changes, and stores a clone that either jumps to the selected event or keeps the current offset.
   *
   * @param isGetPosition - True to scroll to the selected event's start, false to keep the viewer's current offset.
   * @returns Nothing.
   */
  const updateEcgCapture = (isGetPosition = false) => {
    if (_.isEmpty(ecgObjectRef.current)) return;
    const clonedEcgObject = _.cloneDeep(ecgObjectRef.current);
    const { samplingFrequency } = ecgDataMap;
    const selectedEv = getSelectedEvent();
    const findEvent: any = _.find(savedEvents.current, (x) => x?.idEvent === selectedEv?.idEvent);
    const eventsInfo = _.map(savedEvents.current, (event) =>
      getEventInfoInEcgViewer({
        event,
        isSelected: findEvent?.idEvent === event?.idEvent,
        selectedStrip: findEvent,
      }),
    );
    let drawStartSample = 1;
    if (isGetPosition) {
      const startSampleEpoch = moment(findEvent?.start).valueOf();
      if (
        startSampleEpoch >= startStripEpoch.current &&
        startSampleEpoch <= stopStripEpoch.current &&
        findEvent?.type
      ) {
        const additionalSample = (TYPE_BEAT_EVENT_ENUM as any)[findEvent?.type]
          ? 5 * samplingFrequency
          : samplingFrequency;
        const startSample =
          Math.round(((startSampleEpoch - startStripEpoch.current) / 1000) * samplingFrequency) -
          additionalSample;
        drawStartSample = startSample > 1 ? startSample : 1;
      }
    }
    const eventChanges = getEventChanges();
    const selectedEvent = getSelectedEvent();
    const selectedEventIndex = _.findIndex(
      eventChanges,
      (x) => x.idEvent === selectedEvent.idEvent,
    );
    const selectedEventChanges =
      selectedEventIndex > -1 ? eventChanges[selectedEventIndex] : selectedEvent;
    let drawList: any = [];
    if (selectedEventChanges?.type) {
      drawList = selectedEventChanges?.draw;
    } else {
      // isRemovedStrip is read here but IEventOther (Recoil/model.ts, Task 7) never declares it - pre-existing.
      const otherEvents = getEventOthers() as any[];
      const filterRemoveOther = _.filter(otherEvents, (x) => !x.isRemovedStrip);
      drawList =
        _.filter(_.flatten(_.map(filterRemoveOther, (event) => event.draw)), (x) => !!x) || [];
    }
    const { draw } = formatDrawDisplay(
      drawList,
      startStripEpoch.current,
      samplingFrequency,
      stopStripEpoch.current,
    );
    const viewerObject = {
      options: {},
      widgets: draw,
    };
    const { updatedBeats, updatedHesBeatStatus } = generateUpdatedBeatsV2({
      beats: originalBeatMarkers.current,
      beatHeatMapIds: originalBeatHeatMapIds.current,
      hesBeatStatus: originalHesBeatStatus.current,
      beatTemplateIds: originalBeatTemplateIds.current,
      beatChanges: getBeatChanges(),
      startEpoch: startStripEpoch.current,
      stopEpoch: stopStripEpoch.current,
      applyBulkBeatsChanges: getBulkBeatsChanges(),
      resetAppliedBulkBeatsChanges: getResetBulkBeatsChanges(),
    });
    beatMarkersInEcgViewer.current = updatedBeats;
    hesBeatStatusInEcgViewer.current = updatedHesBeatStatus;

    _.assign(clonedEcgObject, {
      viewerObject,
      events: eventsInfo,
      jumpOffset: isGetPosition ? drawStartSample : ecgChartRef.current?.getOffsetX(),
      hiddenChannels: ECG_VIEWER_CONFIG_STORE.visibleChannels,
      hpFilters: ECG_VIEWER_CONFIG_STORE.hiPValue,
      notchFilters: ECG_VIEWER_CONFIG_STORE.loPValue,
      speed: ECG_VIEWER_CONFIG_STORE.spdValue,
      amp: ECG_VIEWER_CONFIG_STORE.ampValue,
      wndDuration: ECG_VIEWER_CONFIG_STORE.wndDuration,
      aiPredict: [
        {
          beats: updatedBeats,
          hesBeatStatus: updatedHesBeatStatus,
          channel: 1,
          hesEventStatus: _.times(updatedHesBeatStatus.length, () => 1),
        },
      ],
    });
    setEcgObject(clonedEcgObject);
    ecgObjectRef.current = clonedEcgObject;
  };
  useEffect(() => {
    updateEcgCapture(true);
  }, [selectedEvent.idEvent]);
  useEmitter(EMITTER_CONSTANTS.UPDATE_CAPTURE_EVENT, updateEcgCapture, [ecgObject]);

  const handleCaptureImages = ({
    draw,
    allIndexStripSvg,
    technicianComments,
    captureMode,
    captureTitle,
    visibleChannels,
  }: {
    draw: any;
    allIndexStripSvg: any;
    technicianComments: any;
    captureMode: any;
    captureTitle: any;
    visibleChannels: any;
  }) => {
    try {
      if (allIndexStripSvg.length > 0) {
        if (captureMode === CAPTURE_MODE.EVENT) {
          preventReloadEcgViewer.current = true;
          if (selectedEvent.type === TYPE_RHYTHM_EVENT_ENUM.AFIB) {
            keepEcgPosition.current = true;
          }
          const { samplingFrequency } = ecgDataMap;
          const { formatDraw } = formatDrawMutation(
            draw,
            samplingFrequency,
            startStripEpoch.current,
            timezoneOffset,
            visibleChannels,
          );
          if (selectedEvent.isNew) {
            const data = updateListImagesChangeV2(
              getEventNews(),
              selectedEvent,
              formatDraw,
              allIndexStripSvg,
              technicianComments,
            );
            // Perform grouping event action applicable to undo/redo
            setEventOptions((prevOptions: any) => ({
              ...prevOptions,
              eventNewsState: data,
            }));
            setEventNews(data);
          } else {
            const data = updateListImagesChangeV2(
              getEventChanges(),
              selectedEvent,
              formatDraw,
              allIndexStripSvg,
              technicianComments,
            );
            setEventOptions((prevOptions: any) => ({
              ...prevOptions,
              eventChangesState: data,
            }));
            setEventChanges(data);
          }
          toastrSuccess(
            'You can find captured strip in "Strips Management" tab.',
            'Strip captured',
          );
          updateEcgCapture();
        }
        if (captureMode === CAPTURE_MODE.OTHER) {
          const { samplingFrequency } = ecgDataMap;
          const { formatDraw, stripStart, stripStop } = formatDrawMutation(
            draw,
            samplingFrequency,
            startStripEpoch.current,
            timezoneOffset,
            visibleChannels,
          );
          const startTime = stripStart;
          const endTime = stripStop;
          const data = handleAddEventOthersV2({
            events: getEventOthers(),
            draw: formatDraw,
            allIndexStripSvg,
            technicianComments,
            captureTitle,
            startTime,
            endTime,
            miniteInfo: getMinuteInfoEvents(),
          });
          // Perform grouping event action applicable to undo/redo
          setEventOptions((prevOptions: any) => ({
            ...prevOptions,
            eventOthersState: data,
          }));
          setEventOthers(data);
          updateEcgCapture();
        }
      }
    } catch (error) {
      logError('Failed to capture images: ', error);
      toastrError(error.message, 'Error');
    }
  };

  /**
   * Records a beat edit made inside the ECG viewer into the tab's pending beat changes. Diffs the incoming markers and HES statuses against the ones currently drawn, reverts any bulk beat changes the edit invalidates, merges the resulting beat changes into the store and mirrors both into the undo/redo beat options.
   *
   * @param updatedBeatObject - The viewer's edit payload carrying currentBeatMarkers and currentHesBeatStatus.
   * @returns Nothing.
   */
  const handleUpdateBeat = (updatedBeatObject: any) => {
    const { currentBeatMarkers, currentHesBeatStatus } = updatedBeatObject;

    const updatedBeatMarkers = getUpdatedBeatMarkerV2(
      currentBeatMarkers,
      _.isEmpty(beatMarkersInEcgViewer.current)
        ? originalBeatMarkers.current
        : beatMarkersInEcgViewer.current,
      currentHesBeatStatus,
      _.isEmpty(hesBeatStatusInEcgViewer.current)
        ? originalHesBeatStatus.current
        : hesBeatStatusInEcgViewer.current,
      originalHesBeatStatus.current,
      startStripEpoch.current,
      originalBeatHeatMapIds.current,
      originalBeatMarkers.current,
    );
    beatMarkersInEcgViewer.current = currentBeatMarkers;
    hesBeatStatusInEcgViewer.current = currentHesBeatStatus;

    const resetBulkChanges = handleResetAppliedBuldBeats({
      appliedBuldBeats: getResetBulkBeatsChanges(),
      strips: updatedBeatMarkers,
      type: updatedBeatMarkers?.[0]?.type,
      bulkBeatsChanges: getBulkBeatsChanges(),
    });

    let beatsChanges: any[] = [];
    setBeatChanges((currBeatChange: any) => {
      // handleUpdateBeatChanges only declares 2 params; this call site passes a 3rd
      // (resetBulkChanges), which JS silently ignores - pre-existing.
      const beats = (handleUpdateBeatChanges as any)(
        updatedBeatMarkers,
        currBeatChange,
        resetBulkChanges,
      );
      beatsChanges = beats;
      currentBeatsChangeRef.current = beats;
      return beats;
    });
    setResetBulkBeatsChanges(resetBulkChanges);

    setBeatOptions((prev: any) => ({
      ...prev,
      beatChangesState: beatsChanges,
      resetBulkBeatsChangesState: resetBulkChanges,
    }));
  };

  /**
   * Steps the AFib ECG viewer to the neighbouring minute of the recording. Flags the viewer to land at the start of the strip when moving forward and at its end when moving backward, then forwards the request to the parent's arrow handler.
   *
   * @param isNext - True to move to the next minute, false to move to the previous one.
   * @returns Nothing.
   */
  const clickArrowButtonHandle = useCallback((isNext: boolean) => {
    if (isNext) {
      isJumpToStart.current = true;
    } else {
      isJumpToEnd.current = true;
    }
    handleClickArrowButton(isNext);
  }, []);

  /**
   * Decides which capture mode the strip toolbar should offer for the currently selected event. Artifact and deleted-beat events capture as artifacts, any other typed event captures as an event, and a selection with no type falls back to the free-form other mode.
   *
   * @returns One of the CAPTURE_MODE values: ARTIFACT, EVENT or OTHER.
   */
  const getCaptureMode = () => {
    if (selectedEvent?.type) {
      if (
        selectedEvent.type === TYPE_RHYTHM_EVENT_ENUM.ARTIFACT ||
        selectedEvent.type === TYPE_BEAT_ENUM.DELETED
      ) {
        return CAPTURE_MODE.ARTIFACT;
      }
      return CAPTURE_MODE.EVENT;
    }
    return CAPTURE_MODE.OTHER;
  };
  useDeepCompareEffect(
    () => {
      isActiveQuery = true;
      if (isRenderViewerEcg) {
        downloadDataEcgV2(selectedColumnInfo, selectedEventIndex);
      }
    },
    [selectedColumnInfo, selectedEventIndex, eventChanges, reloadEcgViewer, isRenderViewerEcg],
    () => {
      isActiveQuery = false;
    },
  );

  /**
   * Redraws the ECG object in place, keeping the viewer's current horizontal offset. Regenerates beats from the pending, bulk and reset beat changes, rebuilds the strip overlay from the selected event's draw list or from the other events when nothing typed is selected, resets the viewer mode when beats changed, and stores the cloned object.
   *
   * @param isChangeBeats - True when the redraw follows a beat edit, which also resets the chart's interaction mode.
   * @returns Nothing.
   */
  const reDrawEcgObjectKeepPosition = (isChangeBeats?: boolean) => {
    if (!_.isEmpty(ecgObject)) {
      const { updatedBeats, updatedHesBeatStatus } = generateUpdatedBeatsV2({
        beats: originalBeatMarkers.current,
        hesBeatStatus: originalHesBeatStatus.current,
        beatHeatMapIds: originalBeatHeatMapIds.current,
        beatTemplateIds: originalBeatTemplateIds.current,
        beatChanges: getBeatChanges(),
        startEpoch: startStripEpoch.current,
        stopEpoch: stopStripEpoch.current,
        applyBulkBeatsChanges: getBulkBeatsChanges(),
        resetAppliedBulkBeatsChanges: getResetBulkBeatsChanges(),
      });
      beatMarkersInEcgViewer.current = updatedBeats;
      hesBeatStatusInEcgViewer.current = updatedHesBeatStatus;

      const clonedEcgObject = _.cloneDeep(ecgObjectRef.current);
      const { samplingFrequency } = ecgDataMap;
      const eventChanges = getEventChanges();
      const selectedEvent = getSelectedEvent();
      const selectedEventIndex = _.findIndex(
        eventChanges,
        (x) => x.idEvent === selectedEvent.idEvent,
      );
      const selectedEventChanges =
        selectedEventIndex > -1 ? eventChanges[selectedEventIndex] : selectedEvent;
      const otherEvents = getEventOthers();
      const drawList = selectedEventChanges?.type
        ? selectedEventChanges?.draw
        : _.filter(_.flatten(_.map(otherEvents, (event) => event.draw)), (x) => !!x) || [];
      const { draw } = formatDrawDisplay(
        drawList,
        startStripEpoch.current,
        samplingFrequency,
        stopStripEpoch.current,
      );

      const viewerObject = {
        options: {},
        widgets: draw,
      };

      _.assign(clonedEcgObject, {
        viewerObject,
        jumpOffset: ecgChartRef.current?.getOffsetX(),
        hiddenChannels: ECG_VIEWER_CONFIG_STORE.visibleChannels,
        hpFilters: ECG_VIEWER_CONFIG_STORE.hiPValue,
        notchFilters: ECG_VIEWER_CONFIG_STORE.loPValue,
        speed: ECG_VIEWER_CONFIG_STORE.spdValue,
        amp: ECG_VIEWER_CONFIG_STORE.ampValue,
        wndDuration: ECG_VIEWER_CONFIG_STORE.wndDuration,
        aiPredict: [
          {
            beats: updatedBeats,
            hesBeatStatus: updatedHesBeatStatus,
            channel: 1,
            hesEventStatus: _.times(updatedHesBeatStatus.length, () => 1),
          },
        ],
      });
      if (isChangeBeats) {
        if (ecgChartRef.current) {
          ecgChartRef.current.resetInMode(true);
        }
      }
      setEcgObject(clonedEcgObject);
      ecgObjectRef.current = clonedEcgObject;
    }
  };

  // Re-create ECG object when beat changes
  useUpdateEffect(() => {
    if (isRenderViewerEcg) {
      reDrawEcgObjectKeepPosition();
    }
  }, [ecgBookmarks, eventOthers, isRenderViewerEcg]);

  useUpdateEffect(() => {
    if (!_.isEqual(beatChanges, currentBeatsChangeRef.current)) {
      currentBeatsChangeRef.current = beatChanges;
      reDrawEcgObjectKeepPosition(true);
    }
  }, [beatChanges]);

  useUpdateEffect(() => {
    if (isRenderViewerEcg) {
      reDrawEcgObjectKeepPosition();
    }
  }, [eventFilter, isRenderViewerEcg]);

  useUpdateEffect(() => {
    if (!isActiveTab) {
      reCreateObjectEcg();
    } else if (ecgChartRef.current) {
      // resetInMode's declared signature (aiECGViewer.tsx, Task 5) requires isResetBeatAr; this
      // call site relies on the JS-era implicit-undefined-arg behaviour - pre-existing.
      (ecgChartRef.current as any).resetInMode();
    }
  }, [isActiveTab]);

  useUpdateEffect(() => {
    /**
     * Scrolls the ECG viewer to the requested jump timestamp once the chart has finished loading. Converts the timestamp into a sample offset within the current strip window, jumps with the stored portion and clears both jump fields, or retries itself after ten milliseconds while the chart is still loading.
     *
     * @returns Nothing.
     */
    const handleJumpToEcgViewer = () => {
      if (jumpTime <= stopStripEpoch.current && jumpTime >= startStripEpoch.current) {
        if (ecgChartRef.current?.hasLoadChart()) {
          // jumpTimeState (Recoil/index.ts, Task 7) is typed Number | undefined (boxed Number) - pre-existing gap.
          const jumpPos = Math.round(
            (((jumpTime as any) - startStripEpoch.current) / 1000) * ecgDataMap.samplingFrequency,
          );
          const jumpPortion = getJumpPortion();
          ecgChartRef.current.handleJumpTo(jumpPos, jumpPortion);
          setJumpTime(undefined);
          setJumpPortion(0);
        } else {
          setTimeout(() => {
            handleJumpToEcgViewer();
          }, 10);
        }
      }
    };
    if (
      jumpTime &&
      !_.isEmpty(ecgChartRef.current) &&
      startStripEpoch.current &&
      stopStripEpoch.current &&
      !isLoading
    ) {
      handleJumpToEcgViewer();
    }
  }, [
    jumpTime,
    isLoading,
    ecgObject, // trigger useEffect when data has cached
  ]);

  const eventIndicator = useMemo(() => {
    const { samplingFrequency } = ecgDataMap;
    let startPos = null;
    let endPos = null;
    if (pickedStartDate) {
      const pickedStartEpoch = new Date(pickedStartDate).getTime();
      startPos = Math.round(
        ((pickedStartEpoch - startStripEpoch.current) / 1000) * samplingFrequency,
      );
    }
    if (pickedEndDate) {
      const pickedEndEpoch = new Date(pickedEndDate).getTime();
      endPos = Math.round(((pickedEndEpoch - startStripEpoch.current) / 1000) * samplingFrequency);
    }
    return {
      startPos,
      endPos,
    };
  }, [pickedStartDate, pickedEndDate, ecgObject]);

  const bookmarksInRange = useMemo(() => {
    const result: any[] = [];
    _.forEach(ecgBookmarks, (bookmark: any) => {
      if (bookmark.center >= startStripEpoch.current && bookmark.center <= stopStripEpoch.current) {
        result.push(bookmark);
      }
    });
    return result;
  }, [ecgBookmarks, ecgObject]);

  const bookmarkIndicators = useMemo(() => {
    const { samplingFrequency } = ecgDataMap;
    const listBookmarks: any[] = [];
    if (pickedBookmarkDate) {
      const pickedEpoch = new Date(pickedBookmarkDate).getTime();
      listBookmarks.push({
        sample: Math.round(((pickedEpoch - startStripEpoch.current) / 1000) * samplingFrequency),
        id: 'marker',
      });
    }
    _.forEach(bookmarksInRange, (bookmark) => {
      listBookmarks.push({
        sample: Math.round(
          ((bookmark.center - startStripEpoch.current) / 1000) * samplingFrequency,
        ),
        id: bookmark.center,
      });
    });
    return listBookmarks;
  }, [pickedBookmarkDate, bookmarksInRange, ecgObject]);

  useEffect(() => {
    /**
     * Cancels an in-progress start, end or bookmark date pick when Escape is pressed over the ECG viewer. Ignores auto-repeat keys and clears the pick action only while one is active.
     *
     * @param event - The keyup event, read for its key and repeat flag.
     * @returns Nothing.
     */
    const handleKeyup = (event: any) => {
      if (event?.repeat) {
        return;
      }
      const { key } = event;
      if (key === 'Escape' && pickDateAction) {
        setPickDateAction(TPickDateAction.NONE);
      }
    };
    if (isPickingDate) {
      window.addEventListener('keyup', handleKeyup);
    }
    return () => {
      window.removeEventListener('keyup', handleKeyup);
    };
  }, [pickDateAction, isPickingDate]);

  return {
    isLoading,
    isLoadingHrChartOptions,
    isLoadingHrChart,
    ecgObject,
    startStripEpoch,
    ecgChartRef,
    handlePickDate,
    pickDateAction,
    eventIndicator,
    bookmarkIndicators,
    getCaptureMode,
    handleCaptureImages,
    clickArrowButtonHandle,
    handleUpdateBeat,
    aiChannel,
    isDisableNextBtn,
    isDisablePrevBtn,
    isPickingDate,
    selectedEvent,
  };
};

export default useAfibEcgViewerWrapper;
